import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BadgeCheck, Building2, FileText, Loader2, Mail, PenLine, Save } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  COMPANY,
  LANDLORD_TERM_LABEL,
  fillLetter,
  placeholdersIn,
  type LandlordTerm,
} from '@jt/domain';
import { api } from '../lib/api';
import type { LandlordSigningView } from '../lib/types';
import { ContractDocument } from '../components/ContractDocument';
import {
  GAP_ATTRIBUTE,
  InlineField,
  SigningBar,
  inlineFilling,
  type InlineFieldSpec,
} from '../components/InlineFields';
import { PrintContractButton, PrintableContract } from '../components/PrintContract';
import { SigningFields, SigningSequence } from '../components/SigningSequence';
import { useToast } from '../components/Toast';

/**
 * The page a landlord reaches from the link in their email.
 *
 * ## Written for somebody who has never seen this system
 *
 * No rail, no account, no jargon. A landlord opening this is not a user of a
 * dispatch console — they are a person who was asked for permission to run a
 * cable through their building, probably on a phone, probably once. So the
 * page says who is asking, what for, how long, and what happens to the
 * equipment; the boxes it asks them to fill are the four things only they
 * know; and the whole agreement is on the page above the signature rather
 * than behind a link, because nobody should be asked to sign something they
 * have to go and find.
 *
 * ## Why there is no login
 *
 * A landlord is not a customer and will usually never be one. Demanding an
 * account before they can grant permission is how permission never arrives.
 * The token in the address is the authority: scoped to this one agreement,
 * revoked the moment it is signed, and checked on every request.
 */
export function LandlordSignPage() {
  const { token = '' } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();

  const view = useQuery({
    queryKey: ['landlord-sign', token],
    queryFn: () => api.landlordSigningView(token),
    enabled: Boolean(token),
    retry: false,
  });

  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [typedName, setTypedName] = useState('');

  /*
   * The agreement as it came back from signing it.
   *
   * Held here rather than refetched, because **signing revokes this link**.
   * The page used to invalidate the query on success, which refetched
   * `GET /landlord/:token` with the token that had just been spent — so it
   * 404ed, and the landlord, one second after signing successfully, was shown
   * "This link no longer works" underneath a toast saying their copy was on
   * the page. The signature was safely recorded the whole time; only the
   * screen was lying.
   *
   * The response to signing *is* the signed agreement, so there is nothing to
   * go back for. Keeping it means the page never asks a question whose answer
   * it already has and whose credential it has just destroyed.
   */
  const [signedCopy, setSignedCopy] = useState<LandlordSigningView | null>(null);
  const [reviewing, setReviewing] = useState(false);

  const hydrated = useRef<string | null>(null);
  useEffect(() => {
    if (!view.data || hydrated.current === token) return;
    hydrated.current = token;
    setDraft(view.data.values ?? {});
  }, [token, view.data]);

  const save = useMutation({
    mutationFn: () => api.saveLandlordOwnFields(token, draft),
    onSuccess: async () => {
      toast.done('Saved. You can come back to this link and finish later.');
      await queryClient.invalidateQueries({ queryKey: ['landlord-sign', token] });
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  const sign = useMutation({
    mutationFn: () => api.signAsLandlord(token, { typedName, fields: draft }),
    onSuccess: (agreement) => {
      toast.done('Signed. A copy is on its way to your email.');
      setTypedName('');

      /*
       * No invalidation. See `signedCopy` above — the token this query is
       * keyed on no longer exists, so refetching is precisely how the
       * confirmation screen became a dead end.
       */
      setSignedCopy({
        ...(agreement as LandlordSigningView),
        canSign: false,
        refusal: null,
        whoIsNext: null,
      });
    },
    onError: (error: Error) => toast.failed(error.message),
  });

  if (view.isPending) {
    return (
      <div className="py-24 text-center">
        <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary-500" />
      </div>
    );
  }

  /*
   * An expired or cancelled link, said plainly and with a way out. "Not
   * found" to somebody who was emailed a link reads as an accusation; the
   * useful thing is a phone number.
   */
  if ((view.isError || !view.data) && !signedCopy) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 text-center">
        <Building2 className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden="true" />
        <h1 className="mt-4 font-heading text-xl">This link no longer works</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          It may have expired, or the agreement may have been cancelled. If you have signed and
          are looking for your copy, use the link in the email we sent you when you signed. Call{' '}
          {COMPANY.phoneE164} or email {COMPANY.email} and we will send a new one.
        </p>
      </div>
    );
  }

  /*
   * What was signed wins over what the query holds: after signing, the query
   * is keyed on a spent token and will never answer again.
   */
  const it = signedCopy ?? view.data!;
  const justSigned = signedCopy !== null;
  const signed = it.signatures.map((one) => one.party);
  const done = it.status === 'SIGNED';
  /*
   * `canSign` comes from the API and says which kind of link this is: the
   * signing link, or the copy link that replaces it once signed. Asked
   * explicitly rather than inferred from the status, because the two answer
   * different questions — "is this agreement still open?" and "may the person
   * holding this link act on it?" — and a copy link must never render a
   * signing form even if something upstream left the status open.
   */
  const maySign = it.canSign !== false && !it.refusal && !done;

  const date = (iso: string | null) =>
    iso
      ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
      : null;

  /*
   * ── Filling the agreement in, in the agreement ──────────────────────────
   *
   * The landlord's own boxes used to be a grid under twenty-five clauses,
   * labelled "National ID" and "Caretaker's telephone" with no sight of the
   * sentences they belong to. Somebody filling that in is being asked to
   * answer questions about a document while looking away from it.
   *
   * Now the gaps are in the clauses. `leaveOpen` keeps their placeholders
   * unfilled through `fillLetter`, and `slots` puts an input in each one, so
   * clause 14 reads "the landlord's representative is [      ]" with the
   * cursor in the gap. Nothing else about the document changes: the printed
   * copy below, the hash, and the emailed copy all go through `fillLetter`
   * with no `leaveOpen` and never see an input.
   */
  const mine: InlineFieldSpec[] = it.fields
    .filter((field) => field.party === 'counterparty')
    .map((field) => ({
      key: field.key,
      label: field.label,
      kind: field.kind,
      required: field.required,
      hint: field.hint,
      options: field.options,
    }));

  /*
   * The signature counts as a gap.
   *
   * It is the last thing to fill and the only one not in `fields`, so it is
   * added here — which is what lets the bar say "1 of 5 left" honestly and
   * lets "Next blank" walk somebody all the way to the pen rather than
   * stopping one short and leaving them to find it.
   */
  /*
   * Split by what the wording actually contains.
   *
   * Most fields are gaps in the text and are typed into the text. A few have
   * nowhere to go: "Installation fee" is a choice whose *result* is the
   * sentence `{{feeDescription}}`, so there is no `{{feeBasis}}` anywhere to
   * put a dropdown in. Six of this agreement's fields are like that, and the
   * first version of this screen dropped the form outright — which left no way
   * at all to set the fee, the term, or the exclusivity grant.
   *
   * Asked of the body rather than listed here, because the body is editable:
   * a clause rewritten to mention a field directly gains an inline gap for it
   * with nothing to change on this page.
   */
  const gaps = placeholdersIn(it.body);
  const inDocument = mine.filter((field) => gaps.has(field.key));
  const offDocument = it.fields.filter(
    (field) => field.party === 'counterparty' && !gaps.has(field.key),
  );

  const SIGNATURE_KEY = '__typedName';
  const barFields: InlineFieldSpec[] = maySign
    ? [...mine, { key: SIGNATURE_KEY, label: 'Your signature', kind: 'signature', required: true }]
    : [];

  const filling = inlineFilling(barFields, it.values, { ...draft, [SIGNATURE_KEY]: typedName });

  const setField = (key: string, value: string) =>
    setDraft((old) => ({ ...old, [key]: value }));

  const slots = maySign
    ? Object.fromEntries(
        inDocument.map((field) => [
          field.key,
          <InlineField
            key={field.key}
            field={field}
            value={filling.valueOf(field.key)}
            onChange={(value) => setField(field.key, value)}
          />,
        ]),
      )
    : undefined;

  const leaveOpen = maySign ? new Set(inDocument.map((field) => field.key)) : undefined;

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 py-8">
      {/* ── Who is asking, and what for ─────────────────────────────── */}
      <header className="print:hidden">
        <div className="flex items-center gap-3">
          <img src="/brand/logo.png" alt="" className="h-11 w-auto" />
          <div>
            <h1 className="font-heading text-2xl">Building access agreement</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {it.reference} · {it.propertyName}
            </p>
          </div>
        </div>

        <p className="mt-4 max-w-2xl text-sm">
          {COMPANY.legalName} is asking your permission to install and maintain internet equipment
          at <strong>{it.propertyName}</strong>, for{' '}
          <strong>{LANDLORD_TERM_LABEL[it.term as LandlordTerm].toLowerCase()}</strong>. The
          equipment stays ours and we maintain it.{' '}
          {justSigned
            ? 'Both of us have signed it — this is your copy.'
            : done
              ? 'Both of us have signed it — the whole agreement is below, and this is your copy.'
              : 'The whole agreement is below. We have signed it already — your signature completes it.'}
        </p>
      </header>

      {/*
        * The moment after signing.
        *
        * Written as its own panel rather than a line in the existing banner,
        * because this is the one screen a landlord is guaranteed to read: they
        * have just signed away permission to drill their building and want to
        * know two things — did it work, and where is my copy. Both are
        * answered before anything else on the page.
        *
        * The email address is named. "A copy has been sent to your email" is
        * the sentence every phishing message uses; naming the address is what
        * lets somebody check it is theirs, and it is the address printed on
        * the agreement they just read.
        */}
      {justSigned ? (
        <section className="card space-y-3 border-secondary-300 bg-secondary-50 print:hidden">
          <h2 className="flex items-center gap-2 font-heading text-lg text-secondary-900">
            <BadgeCheck className="h-6 w-6 shrink-0 text-secondary-600" aria-hidden="true" />
            Signed — thank you
          </h2>

          <p className="text-sm text-secondary-900">
            Both of us have signed <strong>{it.reference}</strong> for {it.propertyName}. Neither
            side can change it now.
          </p>

          <p className="flex items-start gap-2 rounded-xl bg-background/70 p-3 text-sm">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-secondary-600" aria-hidden="true" />
            <span>
              <strong>A copy is on its way to {it.landlord.email}.</strong> It has a link that
              opens this agreement for a year, and the page it opens has a download button. If it
              does not arrive, check the spam folder and then call us — we will send it again.
            </span>
          </p>

          <div className="flex flex-wrap items-center gap-2 border-t border-secondary-300 pt-3">
            <button
              type="button"
              className="btn-primary"
              onClick={() => setReviewing((open) => !open)}
              aria-expanded={reviewing}
            >
              <FileText className="h-4 w-4" aria-hidden="true" />
              {reviewing ? 'Hide the agreement' : 'Review the agreement'}
            </button>
            <PrintContractButton />
            <span className="text-xs text-muted-foreground">
              Signed on {date(it.signedAt)} by {it.landlord.name}
            </span>
          </div>

          {/*
            * Opened in place rather than navigated to. Somebody who has just
            * signed and wants to re-read clause 5 should not have to leave the
            * page that is telling them it worked — and there is nowhere to
            * navigate to anyway: the link they arrived on is spent, and the
            * one that replaces it is in an email that may not have arrived
            * yet.
            */}
          {reviewing && (
            <div className="max-h-[32rem] overflow-y-auto rounded-xl border border-border bg-background p-4 text-sm">
              <ContractDocument body={fillLetter(it.body, it.values)} />
            </div>
          )}
        </section>
      ) : (
        done && (
          <p className="card flex items-center gap-2 border-secondary-300 bg-secondary-50 text-sm text-secondary-900 print:hidden">
            <BadgeCheck className="h-5 w-5 shrink-0 text-secondary-600" aria-hidden="true" />
            <span>
              Signed on {date(it.signedAt)} by {it.landlord.name}, and by {COMPANY.legalName}.
              Neither side can change it now — use <strong>Download or print</strong> below to keep
              a copy. This page stays here for a year; after that, ask us and we will send it
              again.
            </span>
          </p>
        )
      )}

      {/*
        * Said to the landlord too, and in their words rather than ours.
        *
        * If somebody at GoSpeedNet has changed this after they signed it, the
        * person with the most at stake is the one holding the copy — and they
        * are the one who would otherwise never find out. Their emailed copy is
        * unchanged; this page is the one that can move.
        */}
      {it.integrity && !it.integrity.intact && (
        <section className="card border-danger-300 bg-danger-50 print:hidden">
          <h2 className="flex items-center gap-2 font-heading text-lg text-danger-800">
            <AlertTriangle className="h-5 w-5 shrink-0" aria-hidden="true" />
            This has changed since you signed it
          </h2>
          <p className="mt-1 text-sm text-danger-900">
            The wording below is not exactly what you put your name to. Whatever you were emailed
            when you signed is the version you agreed, and it has not changed. If you did not agree
            to a change, call {COMPANY.phoneE164} before relying on anything here.
          </p>
        </section>
      )}

      <section className="card space-y-3 print:hidden">
        <h2 className="font-heading text-lg">Signing order</h2>
        <SigningSequence
          signed={signed}
          turn={done ? null : it.turn}
          counterparty="landlord"
          signatures={it.signatures}
        />
        {it.refusal && !done && (
          <p className="rounded-xl bg-accent-50 p-3 text-sm text-accent-900">{it.refusal}</p>
        )}
      </section>

      {/*
        * The agreement, always open while there is still something to decide.
        *
        * Hidden once they have just signed, because the confirmation panel
        * above has its own "Review the agreement" — and two copies of the same
        * twenty-five clauses on one page is not a choice, it is a doubt about
        * which one is real.
        */}
      {!justSigned && (
        <section className="card print:hidden">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-heading text-lg">The agreement</h2>
            <PrintContractButton />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            All twenty-five clauses, and both schedules.{' '}
            {maySign
              ? 'Read it through, and type into the highlighted gaps as you reach them. Nothing is sent until you sign at the bottom.'
              : done
                ? 'This is your copy.'
                : 'Please read it before signing.'}
          </p>
          {/*
            * No scroll box while there is filling to do.
            *
            * A `max-h` box with its own scrollbar is fine for reading and
            * wrong for this: the bar that counts the gaps is sticky to the
            * window, so a document scrolling inside a box would move the gaps
            * under a bar that never moves, and "Next blank" would scroll the
            * page rather than the box it is looking in.
            */}
          <div
            className={`mt-2 rounded-xl border border-border bg-background p-4 text-sm ${
              maySign ? '' : 'max-h-[32rem] overflow-y-auto'
            }`}
          >
            <ContractDocument
              body={fillLetter(it.body, { ...it.values, ...draft }, leaveOpen)}
              slots={slots}
            />
          </div>
        </section>
      )}

      {/* ── Their four boxes, and the signature ─────────────────────── */}
      {maySign && (
        <section className="card space-y-4 print:hidden">
          {/*
            * Almost no grid of boxes here any more.
            *
            * Everything the landlord has to supply that the agreement has a
            * blank for is in the clause that asks for it, above. Anything the
            * wording leaves no room for stays here, and then there is the one
            * thing that is not a detail — putting their name to it.
            */}
          {offDocument.length > 0 && (
            <SigningFields
              fields={offDocument}
              values={{ ...it.values, ...draft }}
              party="counterparty"
              onChange={(key, value) => setField(key, value)}
            />
          )}

          <label className="block text-sm">
            <span className="font-heading text-lg">Type your full name to sign</span>
            <input
              {...{ [GAP_ATTRIBUTE]: SIGNATURE_KEY }}
              className="input signature-ink mt-1 w-full text-2xl"
              value={typedName}
              onChange={(event) => setTypedName(event.target.value)}
              placeholder={it.landlord.name}
            />
            <span className="mt-1 block text-xs text-muted-foreground">
              It has to match <strong>{it.landlord.name}</strong>, the name on the agreement.
              Typing it is your signature: the time, the address you signed from and a fingerprint
              of this exact wording are kept with it, which is what makes it binding under Kenya’s
              Business Laws (Amendment) Act.
            </span>
          </label>

          <button
            type="button"
            className="btn-secondary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="h-4 w-4" aria-hidden="true" />
            )}
            Save and finish later
          </button>
        </section>
      )}

      {/*
        * What is left, and the pen — pinned to the bottom of the window.
        *
        * The agreement is long and the answer to "can I sign yet?" must not
        * depend on where somebody has scrolled to. It is also the only place
        * the refusal from the server is worth repeating: a landlord who has
        * missed a gap finds out here rather than by pressing a button that
        * says no.
        */}
      {maySign && (
        <SigningBar
          outstanding={filling.outstanding}
          total={barFields.length}
          onJump={filling.jumpTo}
        >
          <button
            type="button"
            className="btn-primary"
            disabled={typedName.trim().length < 3 || sign.isPending}
            onClick={() => sign.mutate()}
          >
            {sign.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <PenLine className="h-4 w-4" aria-hidden="true" />
            )}
            Sign the agreement
          </button>
        </SigningBar>
      )}

      <footer className="pb-4 text-center text-xs text-muted-foreground print:hidden">
        {COMPANY.legalName} · {COMPANY.phoneE164} · {COMPANY.email}
        <br />
        Any question about this agreement, call us before signing.
      </footer>

      <PrintableContract
        title={it.title}
        reference={it.reference}
        company={COMPANY.legalName}
        body={fillLetter(it.body, it.values)}
        issuedOn={it.sentAt}
        standing={done ? 'Executed by both parties' : 'Awaiting the landlord’s signature'}
        parties={[
          { role: 'The provider', name: COMPANY.legalName },
          {
            role: 'The landlord / property owner',
            name: it.landlord.name,
            lines: [it.propertyName, it.landlord.phone, it.landlord.email],
          },
        ]}
        counterpartyLabel="The landlord / property owner"
        signatures={it.signatures.map((one) => ({
          party: one.party,
          name: one.signerName,
          title: one.signerTitle,
          signedAt: one.signedAt,
        }))}
      />
    </div>
  );
}
